import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Navbar from "../NavBar";
import Footer from "../Footer";

const integrityPoints = [
  "Workflow-aligned: they reflect how work actually happens",
  "Validated: decisions and actions are controlled",
  "Traceable: records and outcomes are auditable",
  "Governed: accountability is built in from the start",
];

const deliveryContexts = [
  "Multi-stakeholder",
  "Compliance-sensitive",
  "Reporting-heavy",
  "Time-critical",
  "High-volume and high-trust",
];

const whatWeBuild = [
  "Workflow and validation",
  "Compliance and auditability",
  "Reporting and dashboards",
  "Multi-stakeholder coordination",
  "Regulated operations and market infrastructure",
  "Traceable transactions or records",
];

const howWeWork = [
  {
    number: "1",
    title: "Structure",
    body: "We define the real workflow, control points, and stakeholder logic before building.",
  },
  {
    number: "2",
    title: "Governance",
    body: "We embed validation, accountability, and traceability into the system.",
  },
  {
    number: "3",
    title: "Execution",
    body: "We deliver platforms that function reliably in live environments.",
  },
];

const whatThisEnables = [
  "Clearer operations",
  "Stronger accountability",
  "Faster, decision-ready reporting",
  "More reliable data",
  "Better coordination across stakeholders",
  "Systems that can be trusted under scrutiny",
];

const whereItMatters = [
  "Public sector programs",
  "Regulatory and compliance systems",
  "Market infrastructure",
  "Donor-backed initiatives",
  "High-trust institutional platforms",
  "Operational systems with multiple stakeholders",
];

const outcomes = [
  ["Fragmented processes", "Structured workflows"],
  ["Weak visibility", "Real-time oversight"],
  ["Manual control", "Governed execution"],
  ["Unstable systems", "Reliable platforms"],
];

export default function Framework() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.replace("#", ""));
    if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  }, [hash]);

  return (
    <section className="w-full relative bg-white text-gray-800">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[520px] bg-[#101722] text-white overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{ backgroundImage: "url('/images/sliders/_dashboard.jpg')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#101722] via-[#101722]/90 to-[#101722]/50" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-20 py-28">
          <p className="text-sm uppercase tracking-[0.2em] text-[#44c4bd] font-semibold mb-5">
            Systems Execution Framework
          </p>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight max-w-4xl">
            Building Digital Systems That Hold Under Real-World Conditions
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-white/75 leading-relaxed">
            We design and deliver governance-grade digital platforms for institutions where
            workflow, control, and accountability must align.
          </p>
          <p className="mt-4 max-w-2xl text-white/70 leading-relaxed">
            Our approach combines{" "}
            <span className="text-[#44c4bd] font-semibold">System Integrity</span>
            {" "}: ensuring the technology is structurally sound with{" "}
            <span className="text-[#44c4bd] font-semibold">System Delivery</span>
            {" "}: ensuring it works in complex, high-stakes environments.
          </p>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="bg-[#f5f7fa] py-16 px-6 lg:px-20 border-b border-gray-100">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-12 items-start">
          <div>
            <p className="text-sm uppercase tracking-[0.18em] text-[#007681] font-bold mb-3">
              Why It Matters
            </p>
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Most systems fail for one of two reasons.
            </h2>
            <p className="text-gray-600 leading-relaxed text-lg">
              The workflow was never properly structured, or the system could not survive real
              operational pressure. In institutional environments, that leads to inconsistent
              processes, weak traceability, poor stakeholder alignment, delayed reporting, and
              broken trust.
            </p>
            <p className="text-gray-600 leading-relaxed text-lg mt-4">
              We solve both sides of the problem: how the system is structured and how it
              performs in reality.
            </p>
          </div>
          <div className="space-y-4">
            {[
              "Inconsistent processes",
              "Weak traceability",
              "Poor stakeholder alignment",
              "Delayed reporting",
              "Broken trust",
            ].map((item) => (
              <div key={item} className="flex items-center gap-3 bg-white border-l-4 border-red-600 p-4 shadow-sm">
                <span className="text-red-600 font-bold text-lg">✕</span>
                <span className="text-gray-700 font-semibold">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* System Integrity */}
      <Band
        id="integrity"
        eyebrow="System Integrity"
        title="Technology only works when it is properly aligned with workflow, validation, and governance."
        body="We design systems that are:"
        items={integrityPoints}
        footnote="This is what keeps systems from looking functional while failing in practice."
        bg="bg-white"
      />

      {/* System Delivery */}
      <section id="delivery" className="bg-[#101722] text-white py-16 px-6 lg:px-20 border-t border-white/10 scroll-mt-20">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-12 items-start">
          <div>
            <p className="text-sm uppercase tracking-[0.18em] text-[#44c4bd] font-bold mb-3">
              System Delivery
            </p>
            <h2 className="text-3xl lg:text-4xl font-bold mb-5">
              A sound system still has to work under real conditions.
            </h2>
            <p className="text-white/70 leading-relaxed text-lg">
              We build platforms for institutions where operations are complex and
              consequences are real.
            </p>
            <p className="mt-4 text-white/60 leading-relaxed">
              Our delivery focus is simple: create systems that remain reliable when exposed
              to real users, real pressure, and real consequences.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {deliveryContexts.map((item) => (
              <div key={item} className="border border-white/15 p-5">
                <span className="text-[#44c4bd] font-bold mr-2">▸</span>
                <span className="text-white/85 font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Build */}
      <Band
        eyebrow="What We Build"
        title="These are not generic software projects. They are institutional systems that must hold."
        body="We work on systems that typically involve:"
        items={whatWeBuild}
        bg="bg-[#f5f7fa]"
      />

      {/* How We Work */}
      <section className="bg-[#1C1F2B] text-white py-20 px-6 lg:px-20">
        <div className="max-w-6xl mx-auto">
          <p className="text-sm uppercase tracking-[0.18em] text-[#44c4bd] font-bold mb-3">
            How We Work
          </p>
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            Our framework brings three disciplines together.
          </h2>
          <p className="text-white/60 mb-10">
            This is how we move from concept to controlled implementation.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {howWeWork.map((step) => (
              <div key={step.number} className="border border-white/15 p-8">
                <p className="text-[#44c4bd] font-bold text-4xl mb-4">{step.number}</p>
                <h3 className="text-xl font-bold mb-3">{step.title}</h3>
                <p className="text-white/70 leading-relaxed">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What This Enables */}
      <Band
        eyebrow="What This Enables"
        title="When systems are built this way, institutions gain real capability."
        body="That is the difference between a platform that exists and a platform that works."
        items={whatThisEnables}
        bg="bg-white"
      />

      {/* Where It Matters */}
      <section className="bg-[#edf3f7] py-16 px-6 lg:px-20 border-t border-gray-100">
        <div className="max-w-6xl mx-auto">
          <p className="text-sm uppercase tracking-[0.18em] text-[#007681] font-bold mb-3">
            Where It Matters
          </p>
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            This framework is built for environments where failure is expensive.
          </h2>
          <p className="text-gray-600 mb-10 text-lg">
            In these environments, the system must do more than function. It must hold up.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {whereItMatters.map((item) => (
              <div key={item} className="bg-white border-l-4 border-[#007681] p-5 shadow-sm">
                <span className="text-gray-800 font-semibold">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Outcome */}
      <section className="bg-white py-16 px-6 lg:px-20 border-t border-gray-100">
        <div className="max-w-6xl mx-auto">
          <p className="text-sm uppercase tracking-[0.18em] text-[#007681] font-bold mb-3">
            The Outcome
          </p>
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            The Systems Execution Framework helps institutions move from disorder to controlled execution.
          </h2>
          <p className="text-gray-500 mb-10">
            It is the operating logic behind the work we do.
          </p>
          <div className="space-y-5">
            {outcomes.map(([before, after]) => (
              <div key={before} className="flex flex-col sm:flex-row sm:items-center gap-3 text-lg">
                <span className="text-gray-400 line-through min-w-[220px]">{before}</span>
                <span className="text-red-600 font-bold text-xl">→</span>
                <span className="font-semibold text-gray-900">{after}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Start Here + CTA */}
      <section className="bg-[#101722] text-white py-20 px-6 lg:px-20">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-sm uppercase tracking-[0.2em] text-[#44c4bd] font-semibold mb-5">
            Start Here
          </p>
          <h2 className="text-3xl md:text-5xl font-bold leading-tight max-w-3xl mx-auto mb-6">
            If your system must support real workflows, multiple stakeholders, and high-stakes
            operations, the work begins with structure.
          </h2>
          <p className="text-white/60 text-lg mb-3">Then governance.</p>
          <p className="text-white/60 text-lg mb-12">Then execution.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/projects"
              className="bg-[#E4002B] text-white px-8 py-4 font-semibold text-sm hover:bg-red-700 transition-colors duration-200"
            >
              View Selected Case Studies →
            </Link>
            <Link
              to="/contact"
              className="border border-white text-white px-8 py-4 font-semibold text-sm hover:bg-white hover:text-[#101722] transition-colors duration-200"
            >
              Request a Conversation →
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </section>
  );
}

function Band({ id, eyebrow, title, body, items, footnote, bg }) {
  return (
    <section id={id} className={`${bg} py-16 px-6 lg:px-20 border-t border-gray-100 scroll-mt-20`}>
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-12">
        <div>
          <p className="text-sm uppercase tracking-[0.18em] text-[#007681] font-bold mb-3">
            {eyebrow}
          </p>
          <h2 className="text-3xl lg:text-4xl font-bold mb-5">{title}</h2>
          {body && <p className="text-gray-600 leading-relaxed text-lg mb-4">{body}</p>}
          {footnote && (
            <p className="text-gray-400 italic text-sm mt-4">{footnote}</p>
          )}
        </div>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {items.map((item) => (
            <li key={item} className="bg-white border border-gray-100 shadow-sm p-5 font-semibold text-gray-800">
              {item}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
